import { useEffect, useRef, ReactNode, MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'full';
  children: ReactNode;
}

/**
 * Modal component - Base modal component for overlays
 * 
 * Features:
 * - Responsive sizing: sm, md, lg, full
 * - Desktop: Modal centered, max-width based on size
 * - Tablet: Modal full-width with padding
 * - Mobile: Full-screen modal
 * - Focus trap for accessibility
 * - Escape key closes modal
 * - Backdrop overlay with click-to-close
 * - ARIA labels and roles
 * 
 * Size mapping:
 * - sm: max-width 400px
 * - md: max-width 600px
 * - lg: max-width 800px
 * - full: full-screen on mobile, max-width 1200px on desktop
 */
export const Modal = ({ isOpen, onClose, title, size = 'md', children }: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);

  // Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus trap: keep focus within modal when open
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (e.shiftKey) {
        // Shift+Tab: if on first element, move to last
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: if on last element, move to first
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);
    // Focus first element when modal opens
    firstFocusableRef.current?.focus();

    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle backdrop click
  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Size classes
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    full: 'max-w-7xl',
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity duration-300"
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className={`
          relative bg-background rounded-lg shadow-lg
          flex flex-col
          transform transition-all duration-300 ease-in-out
          w-full
          ${size === 'full' ? 'h-full md:h-auto md:max-h-[90vh]' : 'max-h-[90vh]'}
          ${sizeClasses[size]}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2
              id="modal-title"
              className="text-h2 font-semibold text-text-primary"
            >
              {title}
            </h2>
            <button
              ref={firstFocusableRef}
              onClick={onClose}
              className="flex-shrink-0 p-2 text-text-secondary hover:text-text-primary hover:bg-background-secondary rounded-md transition-colors"
              aria-label="Close modal"
              type="button"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        )}

        {/* Content (scrollable) */}
        <div className={`flex-1 overflow-y-auto ${title ? 'p-6' : 'p-6'}`}>
          {children}
        </div>
      </div>
    </div>
  );

  // Render modal using React Portal
  return createPortal(modalContent, document.body);
};

